import { ChatMessage } from "./mockData";

export const STORAGE_KEY_PREFIX = "latte-data-query-messages";
export const INPUT_STORAGE_KEY_PREFIX = "latte-data-query-input";
export const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const MAX_STORED_MESSAGES = 100;

interface StoredChat {
  savedAt: number;
  messages: ChatMessage[];
}

export function storageKey(tenantId: string | null, email: string | null) {
  const scope = [email, tenantId].filter(Boolean).join("-");
  return scope ? `${STORAGE_KEY_PREFIX}-${scope}` : STORAGE_KEY_PREFIX;
}

export function inputStorageKey(tenantId: string | null, email: string | null) {
  const scope = [email, tenantId].filter(Boolean).join("-");
  return scope ? `${INPUT_STORAGE_KEY_PREFIX}-${scope}` : INPUT_STORAGE_KEY_PREFIX;
}

export function loadMessages(tenantId: string | null, email: string | null): ChatMessage[] {
  const key = storageKey(tenantId, email);
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return [];
    const parsed = JSON.parse(stored);

    // Legacy format (plain array) — treat as fresh, upgrade on next save
    if (Array.isArray(parsed)) return parsed;

    const record = parsed as StoredChat;
    if (!record || typeof record.savedAt !== "number") return [];
    if (Date.now() - record.savedAt > EXPIRY_MS) {
      localStorage.removeItem(key);
      localStorage.removeItem(inputStorageKey(tenantId, email));
      return [];
    }
    return record.messages || [];
  } catch {
    return [];
  }
}

export function saveMessages(
  messages: ChatMessage[],
  tenantId: string | null,
  email: string | null,
) {
  try {
    const trimmed = messages.slice(-MAX_STORED_MESSAGES);
    const record: StoredChat = { savedAt: Date.now(), messages: trimmed };
    localStorage.setItem(storageKey(tenantId, email), JSON.stringify(record));
  } catch {
    // ignore quota errors
  }
}

/**
 * Remove all data-query chat entries from localStorage (messages + inputs).
 * Called on logout to purge persisted conversation data.
 */
export function purgeAllChatData() {
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (
        k &&
        (k.startsWith(STORAGE_KEY_PREFIX) || k.startsWith(INPUT_STORAGE_KEY_PREFIX))
      ) {
        keys.push(k);
      }
    }
    keys.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

/**
 * Sweep all stored chats and remove any whose savedAt is older than EXPIRY_MS.
 */
export function purgeExpiredChats() {
  try {
    const now = Date.now();
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(STORAGE_KEY_PREFIX)) continue;
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          // Legacy entry with no timestamp — remove as stale
          toRemove.push(k);
          continue;
        }
        if (typeof parsed?.savedAt === "number" && now - parsed.savedAt > EXPIRY_MS) {
          toRemove.push(k);
        }
      } catch {
        toRemove.push(k);
      }
    }
    toRemove.forEach((k) => {
      localStorage.removeItem(k);
      // Also clear the matching input key
      const inputKey = k.replace(STORAGE_KEY_PREFIX, INPUT_STORAGE_KEY_PREFIX);
      localStorage.removeItem(inputKey);
    });
  } catch {
    // ignore
  }
}
